import { el, ensureStyle } from '../dom';
import { icon, type IconName } from './icons';
import { t } from '../../strings';

ensureStyle(
  'nav-bar-css',
  `
  .navbar {
    position: sticky; bottom: 0; z-index: 10;
    display: grid; grid-template-columns: repeat(4, 1fr);
    /* Opaque, so content scrolling underneath doesn't show through the grain. */
    background: var(--paper); border-top: 1.5px solid var(--hairline);
    padding: 8px 4px calc(8px + env(safe-area-inset-bottom));
  }
  .navbar a {
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    text-decoration: none; color: var(--ink-soft);
    /* 13px * .72em from .label = ~9.4px. Four small-caps labels in a fixed
       4-column grid is the tightest constraint in the app, so the tracking is
       deliberately tighter here than the .label default. */
    font-size: 13px;
    padding: 5px 0 3px; border-radius: 8px;
  }
  .navbar a .label { color: inherit; letter-spacing: .09em; }
  /* Colour instead of a bolder weight — the small-caps face changes width when
     it bolds, which would jitter the grid on navigation. Label, icon and rule
     all take the accent, so the active tab reads as one gold mark. */
  .navbar a.active { color: var(--accent); }
  .navbar a.active::after { content:''; width:16px; height:2px; border-radius:2px;
    background: var(--accent); margin-top:1px; }
`,
);

// Four tabs, and Learn is deliberately not one of them: it was a list of the
// three quiz modes, which is exactly what the Home study grid already is — one
// tap deeper, for nothing. Stats took the slot, since the numbers Home shows
// tersely need somewhere to be explained at length.
const TABS: { path: string; label: string; ico: IconName }[] = [
  { path: '/home', label: t('common.nav.home'), ico: 'home' },
  { path: '/library', label: t('common.nav.library'), ico: 'book' },
  { path: '/stats', label: t('common.nav.stats'), ico: 'chart' },
  { path: '/you', label: t('common.nav.you'), ico: 'user' },
];

export function navBar(): HTMLElement {
  const links = TABS.map((t) =>
    el(
      'a',
      { href: `#${t.path}` },
      icon(t.ico, { size: 21 }),
      el('span.label', {}, t.label),
    ),
  );
  const bar = el('nav.navbar', {}, ...links);

  // Publish the bar's own height, so anything that has to sit CLEAR of it can.
  //
  // The quiz pins its Next button to the foot of the screen once a question is
  // answered, and "the foot of the screen" is the wrong place by exactly this
  // many pixels — the nav bar is sticky at bottom:0 with a z-index above the
  // outlet, so a control pinned to 0 is a control filed underneath it. The
  // number cannot be written down: it is four small-caps labels in whatever
  // font has actually loaded, plus env(safe-area-inset-bottom), which differs
  // per device and changes when an iPhone rotates. So it is measured, and
  // re-measured whenever it moves.
  // tokens.css carries a static --navbar-h close enough to be right before any
  // of this runs, because the first measurement cannot be synchronous: this
  // element is not in the document yet, and a detached element measures zero.
  const publishHeight = () => {
    // Rounded UP, deliberately. The compare view sizes its frame as
    // `100dvh - var(--navbar-h)`, so an over-estimate of a fraction of a pixel
    // costs a fraction of a pixel of frame and an UNDER-estimate costs a
    // scrollbar on a page that should not scroll at all. The bar's real height
    // is fractional (62.5625px at the default font), so this is not
    // hypothetical.
    const h = Math.ceil(bar.getBoundingClientRect().height);
    if (h) document.documentElement.style.setProperty('--navbar-h', `${h}px`);
  };
  // A timer for the first reading and an observer for every one after it. The
  // timer is not redundant: a ResizeObserver's callbacks are delivered from the
  // rendering lifecycle, so a tab that has not painted yet — backgrounded, or
  // restored into an inactive window — can leave the first one arbitrarily
  // late, and until it lands the answered bar would sit behind the nav bar.
  setTimeout(publishHeight, 0);
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(publishHeight).observe(bar);
  }

  const sync = (path: string) => {
    const active = '/' + (path.split('/').filter(Boolean)[0] ?? 'home');
    links.forEach((a, i) => a.classList.toggle('active', TABS[i].path === active));
  };
  window.addEventListener('route', (e) =>
    sync((e as CustomEvent).detail.path as string),
  );
  sync(location.hash.replace(/^#/, '') || '/home');
  return bar;
}
